import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LogOut } from "lucide-react";
import { toast } from "sonner";
import { useDirtyGuard } from "../../context/DirtyGuard";
import { NeuButton, NeuCard } from "../../components/neu/Neu";
import { ResignationConfirm, ResignationFields } from "./ResignationParts";
import { resignationPolicy, submitResignation } from "../../api/hr";
import { useApiError } from "../../hooks/useApiError";

export const ResignationForm = ({ onDone }) => {
  const { t } = useTranslation();
  const describe = useApiError();
  const [policy, setPolicy] = useState(null);
  const [form, setForm] = useState({ reason: "", requested_last_day: "" });
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    (async () => setPolicy(await resignationPolicy()))();
  }, []);

  const persist = useCallback(async () => {
    const res = await submitResignation({
      reason: form.reason || null,
      requested_last_day: form.requested_last_day || null,
    });
    toast.success(t("resignation.submitted", { date: res.last_working_day }));
  }, [form, t]);

  useDirtyGuard(Boolean(form.reason || form.requested_last_day), persist);

  if (!policy) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const res = await submitResignation({
        reason: form.reason || null,
        requested_last_day: form.requested_last_day || null,
      });
      toast.success(t("resignation.submitted", { date: res.last_working_day }));
      setConfirming(false);
      onDone();
    } catch (error) {
      toast.error(describe(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <NeuCard data-testid="resignation-form">
      <div className="flex items-start justify-between gap-3">
        <p className="label-xs">{t("resignation.title")}</p>
        <LogOut className="h-5 w-5 text-destructive" />
      </div>

      <ResignationFields policy={policy} form={form} setForm={setForm} />

      {confirming ? (
        <ResignationConfirm busy={busy} onConfirm={submit} onCancel={() => setConfirming(false)} />
      ) : (
        <NeuButton variant="danger" className="mt-5" data-testid="resignation-submit" onClick={() => setConfirming(true)}>
          {t("resignation.submit")}
        </NeuButton>
      )}
    </NeuCard>
  );
};
